/*
 * Closest-size fallback for the variant picker
 * ---------------------------------------------------------------------------
 * When a shopper changes colour (or any non-size option) and their current size
 * doesn't exist in that colour, or is sold out, this switches to the closest
 * size that is in stock. It runs before the theme handles the change, so the
 * theme only ever loads a valid variant. That keeps the price, ATC button and
 * variant gallery all correct.
 *
 * - Only runs when a non-size option changes. If a shopper clicks a sold-out
 *   size themselves, it's left alone.
 * - "Closest": ranked by standard apparel order (XXS..4XL, youth sizes, numeric
 *   sizes), not the store's option order. On a tie it picks the larger size.
 * - Works on product pages and quick-buy modals (any <variant-picker handle="...">).
 */
(() => {
  if (window.__closestSizeFallback) return;
  window.__closestSizeFallback = true;

  // Set to false to switch only when the combination doesn't exist (leave sold-out sizes selected)
  const SWITCH_WHEN_SOLD_OUT = true;

  const SIZE_OPTION_NAME = /size/i;
  const SIZE_ORDER = [
    'YXXS', 'YXS', 'YS', 'YM', 'YL', 'YXL', 'YXXL',
    'XXXS', 'XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', 'XXXXL', 'XXXXXL'
  ];
  const ALIASES = {
    '3XS': 'XXXS', '2XS': 'XXS', '2XL': 'XXL', '3XL': 'XXXL', '4XL': 'XXXXL', '5XL': 'XXXXXL',
    'XSMALL': 'XS', 'SMALL': 'S', 'MEDIUM': 'M', 'LARGE': 'L', 'XLARGE': 'XL', 'XXLARGE': 'XXL',
    'EXTRASMALL': 'XS', 'EXTRALARGE': 'XL',
    'YOUTHXS': 'YXS', 'YOUTHS': 'YS', 'YOUTHM': 'YM', 'YOUTHL': 'YL', 'YOUTHXL': 'YXL',
    'YOUTHSMALL': 'YS', 'YOUTHMEDIUM': 'YM', 'YOUTHLARGE': 'YL'
  };

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const products = new Map(); // handle -> product JSON
  const pending = new Map();

  function load(handle) {
    if (!handle || products.has(handle) || pending.has(handle)) return;
    const req = fetch(`${root}products/${encodeURIComponent(handle)}.js`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (data) products.set(handle, data); })
      .catch(() => {})
      .finally(() => pending.delete(handle));
    pending.set(handle, req);
  }

  function scan() {
    document.querySelectorAll('variant-picker[handle]').forEach((vp) => load(vp.getAttribute('handle')));
  }

  const norm = (s) => String(s).toUpperCase().replace(/[\s\-_.\/]/g, '');

  function sizeRank(value, allValues) {
    const n = ALIASES[norm(value)] || norm(value);
    const i = SIZE_ORDER.indexOf(n);
    if (i > -1) return i;
    const num = parseFloat(String(value).replace(',', '.'));
    if (!isNaN(num)) return 1000 + num;
    return 5000 + allValues.indexOf(value);
  }

  function optionName(option) {
    return typeof option === 'string' ? option : option.name;
  }

  function optionValues(option) {
    return Array.isArray(option.values) ? option.values : [];
  }

  // Capture phase on window: runs before the theme's delegated change handler
  window.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
    if (!input.matches('input[data-option-position][form]') || input.hasAttribute('data-product-url')) return;

    const formId = input.getAttribute('form');
    const picker = document.querySelector(`variant-picker[form-id="${CSS.escape(formId)}"]`);
    if (!picker) return;

    const product = products.get(picker.getAttribute('handle'));
    if (!product || !product.variants || product.variants.length < 2) return;

    const options = product.options || [];
    const sizeIdx = options.findIndex((o) => SIZE_OPTION_NAME.test(optionName(o)));
    const changedIdx = parseInt(input.dataset.optionPosition, 10) - 1;
    if (sizeIdx === -1 || changedIdx === sizeIdx) return;

    // Radio groups per option, in the same order as product.options[i].values
    const groups = options.map((_, i) =>
      Array.from(document.querySelectorAll(
        `input[type="radio"][data-option-position="${i + 1}"][form="${CSS.escape(formId)}"]`
      ))
    );
    if (groups.some((g, i) => g.length !== optionValues(options[i]).length)) return;

    const selected = groups.map((g, i) => optionValues(options[i])[g.findIndex((r) => r.checked)]);
    if (selected.some((v) => v === undefined)) return;

    const matches = (variant, ignoreIdx) =>
      variant.options.every((val, i) => i === ignoreIdx || val === selected[i]);

    const exact = product.variants.find((v) => matches(v, -1));
    if (exact && (exact.available || !SWITCH_WHEN_SOLD_OUT)) return;

    const sameColour = product.variants.filter((v) => matches(v, sizeIdx));
    if (!sameColour.length) return;

    const inStock = sameColour.filter((v) => v.available);
    if (!inStock.length && exact) return; // nothing better than the sold-out size they had
    const candidates = inStock.length ? inStock : sameColour;

    const sizeValues = optionValues(options[sizeIdx]);
    const currentRank = sizeRank(selected[sizeIdx], sizeValues);

    let best = null;
    let bestDist = Infinity;
    let bestRank = -Infinity;
    candidates.forEach((v) => {
      const r = sizeRank(v.options[sizeIdx], sizeValues);
      const d = Math.abs(r - currentRank);
      if (d < bestDist || (d === bestDist && r > bestRank)) {
        best = v; bestDist = d; bestRank = r;
      }
    });
    if (!best) return;

    const sizeName = best.options[sizeIdx];
    const target = groups[sizeIdx][sizeValues.indexOf(sizeName)];
    if (target && !target.checked) target.checked = true;

    // Keep GLO Color Swatch's visible size buttons in sync with the theme's (hidden) picker
    syncGlo(picker, sizeIdx, sizeName);

    // Neighbouring size: switch and say so. Further away: ask the shopper to choose.
    const ordered = [...sizeValues].sort((a, b) => sizeRank(a, sizeValues) - sizeRank(b, sizeValues));
    const steps = Math.abs(ordered.indexOf(sizeName) - ordered.indexOf(selected[sizeIdx]));
    if (steps > 1) {
      requireSizeChoice(picker, sizeIdx, selected[sizeIdx], selected[changedIdx], !!exact);
    } else {
      showSwapNote(picker, sizeIdx, selected[sizeIdx], sizeName, selected[changedIdx], !!exact);
    }
  }, true);

  // Messages shown when the shopper's size isn't available in the colour they picked.
  //  - Neighbouring size available: we switch to it and say so.
  //  - Nearest size is further away: we ask them to choose, and block Add to Cart /
  //    Buy with Shop until they pick a size themselves.
  const NOTE_CLASS = 'size-fallback-note';
  const SIZE_GUIDE_URL = 'https://cdn.shopify.com/s/files/1/0516/8994/7309/files/Size-Charts_compressed.pdf';
  const noteStyle = document.createElement('style');
  noteStyle.textContent = `.${NOTE_CLASS} { margin: 8px 0 0; font-size: 13px; line-height: 1.4; color: #555; }`;
  document.head.appendChild(noteStyle);
  const requireStyle = document.createElement('style'); // filled only while a size choice is required
  document.head.appendChild(requireStyle);
  let required = null; // { formId }
  let activeNote = null; // { formId, sizeIdx, node } - re-inserted if the theme redraws the picker

  function sizeGuideLink() {
    const guide = document.createElement('a');
    guide.href = SIZE_GUIDE_URL;
    guide.target = '_blank';
    guide.rel = 'noopener';
    guide.textContent = 'size guide';
    guide.style.cssText = 'color: inherit; text-decoration: underline;';
    return guide;
  }

  function clearSwapNotes() {
    document.querySelectorAll(`.${NOTE_CLASS}`).forEach((n) => n.remove());
    requireStyle.textContent = '';
    required = null;
    activeNote = null;
  }

  // Globo's size buttons on the product page; the theme's own picker elsewhere (e.g. quick buy)
  function insertNote(picker, sizeIdx, node) {
    const scope = picker.closest('.quick-buy, quick-buy-modal, .product, [id^="shopify-section"]') || document;
    const gloInput = picker.closest('quick-buy-modal, .quick-buy')
      ? null
      : scope.querySelector(`input[name="globo-option-${sizeIdx}"]`);
    const anchor =
      (gloInput && (gloInput.closest('.swatch--gl') || gloInput.closest('ul'))) ||
      picker.querySelectorAll('fieldset')[sizeIdx];
    if (!anchor || !anchor.parentNode) return;
    anchor.parentNode.insertBefore(node, anchor.nextSibling);
  }

  function placeNote(picker, sizeIdx, parts) {
    const note = document.createElement('p');
    note.className = NOTE_CLASS;
    note.setAttribute('role', 'status');
    note.append(...parts);
    insertNote(picker, sizeIdx, note);
    activeNote = { formId: picker.getAttribute('form-id'), sizeIdx, node: note };
  }

  // The theme redraws the picker after a change, which can remove the message - put it back
  let noteTimer;
  new MutationObserver(() => {
    if (!activeNote) return;
    clearTimeout(noteTimer);
    noteTimer = setTimeout(() => {
      if (!activeNote || document.contains(activeNote.node)) return;
      const picker = document.querySelector(`variant-picker[form-id="${CSS.escape(activeNote.formId)}"]`);
      if (picker) insertNote(picker, activeNote.sizeIdx, activeNote.node);
    }, 50);
  }).observe(document.documentElement, { childList: true, subtree: true });

  function showSwapNote(picker, sizeIdx, fromSize, toSize, otherValue, existed) {
    clearSwapNotes();
    if (!fromSize || !toSize || fromSize === toSize) return;
    placeNote(picker, sizeIdx, [
      `${fromSize} is ${existed ? 'sold out' : 'not available'} in ${otherValue}, so we've selected ${toSize} as the nearest recommended available size. Please refer to our `,
      sizeGuideLink(),
      ' for further information.',
    ]);
  }

  function requireSizeChoice(picker, sizeIdx, fromSize, otherValue, existed) {
    clearSwapNotes();
    const formId = picker.getAttribute('form-id');
    if (!formId) return;
    required = { formId };
    const f = CSS.escape(formId);
    const vp = `variant-picker[form-id="${formId}"] fieldset:nth-of-type(${sizeIdx + 1})`;
    requireStyle.textContent = `
      ${vp} .variant-picker__option-info .h-stack > span { display: none !important; }
      ${vp} legend::after { content: " Please select a size"; }
      ${vp} input:checked + label {
        border-color: rgb(230, 230, 230) !important; box-shadow: none !important; color: rgba(0, 0, 0, .65) !important;
      }
      .swatch--gl[data-option-index="${sizeIdx}"] .name-variant { display: none !important; }
      .swatch--gl[data-option-index="${sizeIdx}"] .name-option::after { content: ": Please select a size"; }
      .swatch--gl[data-option-index="${sizeIdx}"] input:checked + label {
        background: #fff !important; color: #000 !important; border-color: rgb(161, 161, 161) !important;
      }
      .variant-sku { visibility: hidden; }
      #${f} button[type="submit"], button[form="${formId}"], #${f} .shopify-payment-button {
        opacity: .4 !important; pointer-events: none !important; cursor: not-allowed !important;
      }
    `;
    placeNote(picker, sizeIdx, [
      `${fromSize} is ${existed ? 'sold out' : 'unavailable'} in this colour. See our `,
      sizeGuideLink(),
      ' for help choosing a size.',
    ]);
  }

  // Safety net: no adding to cart while a size choice is required
  ['click', 'submit'].forEach((type) =>
    document.addEventListener(
      type,
      (event) => {
        if (!required) return;
        const t = event.target;
        if (!t.closest) return;
        const form = document.getElementById(required.formId);
        const hit =
          (form && form.contains(t) && (type === 'submit' || t.closest('button[type="submit"], .shopify-payment-button'))) ||
          t.closest(`button[form="${required.formId}"]`);
        if (hit) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      },
      true
    )
  );

  // Remove the message as soon as the shopper picks a size or colour themselves
  document.addEventListener(
    'click',
    (event) => {
      if (!event.isTrusted) return;
      if (event.target.closest && event.target.closest(`.${NOTE_CLASS}`)) return; // let the size guide link work
      const t = event.target;
      if (t.closest && t.closest('.globo-swatch-list li, variant-picker')) clearSwapNotes();
    },
    true
  );

  function syncGlo(picker, optionIdx, value) {
    const scope = picker.closest('.product, .quick-buy, [id^="shopify-section"]') || document;
    const gloInput = Array.from(scope.querySelectorAll(`input[name="globo-option-${optionIdx}"]`))
      .find((i) => i.value === value);
    if (!gloInput || gloInput.checked) return;
    // Let the theme start loading the corrected variant first, then update GLO's UI
    setTimeout(() => gloInput.click(), 0);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  // Pick up quick-buy modals and re-rendered pickers
  let scanTimer;
  new MutationObserver(() => {
    clearTimeout(scanTimer);
    scanTimer = setTimeout(scan, 150);
  }).observe(document.documentElement, { childList: true, subtree: true });
})();